// Ever After — Compatibility Assessment (26 questions)
// Phase 1: 16 Likert scoring questions (compatibility + invisibly-mixed bias checks)
// Phase 2: 10 multiple-choice screening questions (disqualification logic on backend)
//
// Internal codes (never shown to user):
//   EI1-4 = Emotional intelligence (EI4 reverse-scored)
//   CR1-2 = Commitment readiness
//   H1    = Honesty-Humility
//   C1    = Conscientiousness
//   A1    = Agreeableness
//   AT1   = Trust   AT2 = Abandonment (reverse-scored)
//   LG1   = Long-term goals
//   BC1-2 = Bias check (lie-scale; flag if both =5) — INVISIBLY MIXED IN
//   RP1-2 = Relationship patterns
//   S1-S10 = Screening (multiple-choice; correct answers held only on backend)

export const LIKERT = [
    { value: 5, label: "Strongly me" },
    { value: 4, label: "Mostly me" },
    { value: 3, label: "Neutral" },
    { value: 2, label: "Not really me" },
    { value: 1, label: "Not me at all" },
];

// Phase 1 — Likert (1-5). Order is intentional: bias-checks are scattered, not bunched.
export const PHASE_1 = [
    { id: "EI1", type: "likert", prompt: "I can accurately identify my own emotions." },
    { id: "H1",  type: "likert", prompt: "I avoid using manipulation to get what I want." },
    { id: "EI2", type: "likert", prompt: "I easily recognize emotional changes in my partner." },
    { id: "AT1", type: "likert", prompt: "I find it easy to trust my partner." },
    { id: "C1",  type: "likert", prompt: "I follow through on commitments reliably." },
    { id: "BC1", type: "likert", prompt: "I never lose patience in relationships." },           // INVISIBLE BIAS CHECK
    { id: "EI3", type: "likert", prompt: "I regulate my emotions well during disagreements." },
    { id: "CR1", type: "likert", prompt: "I am actively seeking a long-term relationship." },
    { id: "A1",  type: "likert", prompt: "I remain respectful in conflict." },
    { id: "EI4", type: "likert", prompt: "I often find it hard to understand others' emotions." }, // reverse
    { id: "RP1", type: "likert", prompt: "I am comfortable in relationships without long-term intent." },
    { id: "LG1", type: "likert", prompt: "Long-term plans should be discussed early." },
    { id: "AT2", type: "likert", prompt: "I worry about being abandoned." },                     // reverse
    { id: "BC2", type: "likert", prompt: "I have never made mistakes in relationships." },       // INVISIBLE BIAS CHECK
    { id: "CR2", type: "likert", prompt: "I make dating decisions based on future compatibility." },
    { id: "RP2", type: "likert", prompt: "My dating history reflects exploration over commitment." },
];

// Phase 2 — Screening (multiple choice A-D). Correct-answer logic lives on the backend.
export const PHASE_2 = [
    {
        id: "S1",
        prompt: "How often do you find yourself in direct conflict with supervisors, teachers, or law enforcement?",
        options: [
            { key: "A", label: "Never or almost never" },
            { key: "B", label: "Occasionally, but usually with good reason" },
            { key: "C", label: "Frequently; I often feel they are unfairly targeting me" },
            { key: "D", label: "Constantly; I don't believe they have the right to tell me what to do" },
        ],
    },
    {
        id: "S2",
        prompt: "When you want something, how much do the \"rules\" or \"laws\" influence your decision to get it?",
        options: [
            { key: "A", label: "I always follow the rules strictly" },
            { key: "B", label: "I follow them unless there is an extreme emergency" },
            { key: "C", label: "I see rules as suggestions that can be bypassed if I'm clever enough" },
            { key: "D", label: "I ignore rules entirely if they get in the way of what I want" },
        ],
    },
    {
        id: "S3",
        prompt: "Which best describes your history of childhood or adolescent behavior (before age 15)?",
        options: [
            { key: "A", label: "I was a well-behaved student who rarely got in trouble" },
            { key: "B", label: "I had typical minor \"rebellious\" phases" },
            { key: "C", label: "I was frequently suspended, skipped school, or ran away" },
            { key: "D", label: "I was involved in fights, vandalism, or hurting animals/people" },
        ],
    },
    {
        id: "S4",
        prompt: "When your actions hurt someone else's feelings or finances, how do you typically feel?",
        options: [
            { key: "A", label: "Deeply guilty and I try to make amends immediately" },
            { key: "B", label: "Regretful, though I might be slow to apologize" },
            { key: "C", label: "Neutral; I usually feel they should have been more careful" },
            { key: "D", label: "I don't feel anything; people have to look out for themselves" },
        ],
    },
    {
        id: "S5",
        prompt: "How would you describe your ability to plan for the long-term future?",
        options: [
            { key: "A", label: "I have a detailed 5-year plan and stick to it" },
            { key: "B", label: "I have general goals I am working toward" },
            { key: "C", label: "I rarely plan more than a few days ahead" },
            { key: "D", label: "I act entirely on impulse; I never think about tomorrow" },
        ],
    },
    {
        id: "S6",
        prompt: "How often do you find yourself \"bending the truth\" or \"conning\" others to get what you want?",
        options: [
            { key: "A", label: "Never; honesty is my highest value" },
            { key: "B", label: "Only in very small \"white lies\" to avoid hurting feelings" },
            { key: "C", label: "Often, if it makes my life easier or helps me get ahead" },
            { key: "D", label: "Constantly; I enjoy seeing if I can trick people" },
        ],
    },
    {
        id: "S7",
        prompt: "When you lose your temper or get into a fight, who is usually responsible?",
        options: [
            { key: "A", label: "I take full responsibility for my reactions" },
            { key: "B", label: "It's usually a 50/50 split between me and the other person" },
            { key: "C", label: "I only react because other people are incompetent or annoying" },
            { key: "D", label: "It is always the other person's fault; they \"asked for it\"" },
        ],
    },
    {
        id: "S8",
        prompt: "How do you feel about the concept of \"loyalty\" to friends or family?",
        options: [
            { key: "A", label: "It is the most important bond in my life" },
            { key: "B", label: "I am loyal to a small, select group of people" },
            { key: "C", label: "I am loyal only as long as that person is useful to me" },
            { key: "D", label: "I don't feel any real bond or obligation to anyone" },
        ],
    },
    {
        id: "S9",
        prompt: "How do you react to dangerous or high-risk situations (speeding, drugs, physical risks)?",
        options: [
            { key: "A", label: "I avoid them entirely; I value my safety" },
            { key: "B", label: "I am cautious and only take calculated risks" },
            { key: "C", label: "I find them exciting and seek them out when I'm bored" },
            { key: "D", label: "I crave the rush and often do things that others call \"reckless\"" },
        ],
    },
    {
        id: "S10",
        prompt: "How much do you agree with the statement: \"The world is a cutthroat place where only the strongest/smartest should survive\"?",
        options: [
            { key: "A", label: "Strongly disagree; we should help the vulnerable" },
            { key: "B", label: "Mostly disagree; cooperation is better than competition" },
            { key: "C", label: "Mostly agree; you have to take what you can get" },
            { key: "D", label: "Strongly agree; I will do whatever is necessary to be on top" },
        ],
    },
];

export const TOTAL_QUESTIONS = PHASE_1.length + PHASE_2.length;
